import { useMemo, useState } from "react";
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useTimeTrackerStore, Client } from "../store/timeTrackerStore";
import { sortClientsForPicker } from "../utils/TimeTrackerUtils";
import { booleanCodec, useUrlParam } from "../utils/useUrlState";
import {
  Button,
  Card,
  ConfirmDialog,
  FormField,
  Input,
  Badge,
  useToast,
} from "@lazar/ui";

const showArchivedCodec = booleanCodec(false);

const GRIP_ICON = (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <circle cx="9" cy="6" r="1.6" />
    <circle cx="15" cy="6" r="1.6" />
    <circle cx="9" cy="12" r="1.6" />
    <circle cx="15" cy="12" r="1.6" />
    <circle cx="9" cy="18" r="1.6" />
    <circle cx="15" cy="18" r="1.6" />
  </svg>
);

const ARCHIVE_ICON = (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <path
      d="M3 7h18M5 7v12a2 2 0 002 2h10a2 2 0 002-2V7M9 11h6"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const EDIT_ICON = (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <path
      d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const TRASH_ICON = (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <path
      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const RESTORE_ICON = (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <path
      d="M3 12a9 9 0 1 0 3-6.7L3 8m0 0V3m0 5h5"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// One active-client row, draggable by its grip handle. Only the handle starts
// a drag (the edit/archive buttons stay clickable), and the KeyboardSensor on
// the parent makes the handle reorderable with the keyboard too.
const SortableClientRow = ({
  client,
  onEdit,
  onArchive,
}: {
  client: Client;
  onEdit: (client: Client) => void;
  onArchive: (id: string) => void;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: client.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Card
      sub
      ref={setNodeRef}
      style={style}
      className={`flex items-center justify-between p-3 ${
        isDragging ? "opacity-70 z-10 relative" : ""
      }`}
    >
      <div className="flex items-center min-w-0">
        <Button
          ref={setActivatorNodeRef}
          {...attributes}
          {...listeners}
          icon
          className="mr-2 cursor-grab active:cursor-grabbing touch-none"
          title="Drag to reorder"
          aria-label={`Reorder ${client.name}`}
        >
          {GRIP_ICON}
        </Button>
        <div
          className="w-5 h-5 rounded-full mr-3 border-2 border-ink dark:border-cream flex-shrink-0"
          style={{ backgroundColor: client.color }}
        />
        <div className="flex flex-col min-w-0">
          <span className="font-bold text-ink dark:text-cream truncate">
            {client.name}
          </span>
          <span className="text-xs text-ink-soft dark:text-cream/60">
            Target: {client.weeklyTargetDays || "N/A "}d/week
          </span>
        </div>
      </div>

      <div className="flex gap-1.5">
        <Button
          onClick={() => onEdit(client)}
          icon
          title="Edit"
          aria-label="Edit client"
        >
          {EDIT_ICON}
        </Button>

        <Button
          onClick={() => onArchive(client.id)}
          icon
          title="Archive"
          aria-label="Archive client"
        >
          {ARCHIVE_ICON}
        </Button>
      </div>
    </Card>
  );
};

const ClientManager = () => {
  const { addToast } = useToast();
  const {
    getAllClients,
    addClient,
    updateClient,
    reorderPickerClients,
    archiveClient,
    unarchiveClient,
    deleteClient,
    getTimeEntries,
  } = useTimeTrackerStore();

  const allClients = getAllClients();
  const { activeClients, archivedClients } = useMemo(() => {
    const active: Client[] = [];
    const archived: Client[] = [];
    for (const c of allClients) {
      if (c.archivedAt === undefined) active.push(c);
      else archived.push(c);
    }
    // Active clients render in picker order — this list IS the control surface
    // for the stop-timer picker, so dragging rows here sets that order. With no
    // pickerOrder set, sortClientsForPicker falls back to alphabetical, so the
    // list looks unchanged until the user drags.
    const activeSorted = sortClientsForPicker(active);
    // Most-recently archived on top — that's typically what users want to restore.
    archived.sort((a, b) => (b.archivedAt ?? 0) - (a.archivedAt ?? 0));
    return { activeClients: activeSorted, archivedClients: archived };
  }, [allClients]);

  const sensors = useSensors(
    // A small activation distance lets a click on the grip still register as a
    // click (e.g. focus) before a drag begins, and avoids accidental drags.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = activeClients.map((c) => c.id);
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    reorderPickerClients(arrayMove(ids, oldIndex, newIndex));
  };

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingClientId, setEditingClientId] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useUrlParam(
    "archived",
    showArchivedCodec,
  );
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState("#10b981");
  const [newWeeklyTargetDays, setNewWeeklyTargetDays] = useState<number | "">(
    0,
  );

  const resetForm = () => {
    setNewName("");
    setNewColor("#10b981");
    setNewWeeklyTargetDays(0);
    setShowAddForm(false);
    setEditingClientId(null);
  };

  const startEdit = (client: Client) => {
    setEditingClientId(client.id);
    setNewName(client.name);
    setNewColor(client.color);
    setNewWeeklyTargetDays(client.weeklyTargetDays || 0);
    setShowAddForm(false);
  };

  const handleAddClient = () => {
    if (!newName.trim()) {
      addToast("Client name is required", "warning");
      return;
    }

    const id = `cl_${Date.now()}`;
    addClient({
      id,
      name: newName,
      color: newColor,
      weeklyTargetDays:
        typeof newWeeklyTargetDays === "number" ? newWeeklyTargetDays : 0,
    });

    addToast("Client saved", "success");
    resetForm();
  };

  const handleUpdateClient = () => {
    if (!editingClientId) return;
    if (!newName.trim()) {
      addToast("Client name is required", "warning");
      return;
    }

    updateClient(editingClientId, {
      name: newName,
      color: newColor,
      weeklyTargetDays:
        typeof newWeeklyTargetDays === "number" ? newWeeklyTargetDays : 0,
    });

    addToast("Client saved", "success");
    resetForm();
  };

  const handleArchive = (id: string) => {
    try {
      archiveClient(id);
    } catch (err) {
      const message = err instanceof Error ? err.message : "";
      if (message.includes("timer references it")) {
        addToast(
          "Stop the running timer before archiving this client",
          "warning",
        );
      } else if (message.includes("last active client")) {
        addToast(
          "Can't archive your last active client. Add another first.",
          "warning",
        );
      } else {
        addToast("Could not archive this client", "error");
      }
      return;
    }
    if (editingClientId === id) resetForm();
    addToast("Client archived", "success", {
      action: { label: "Undo", onClick: () => unarchiveClient(id) },
    });
  };

  const handleRestore = (id: string) => {
    unarchiveClient(id);
    addToast("Client restored", "success");
  };

  const deleteTarget = deleteTargetId
    ? allClients.find((c) => c.id === deleteTargetId)
    : null;
  const deleteTargetEntryCount = deleteTargetId
    ? getTimeEntries().filter((e) => e.clientId === deleteTargetId).length
    : 0;

  const confirmDelete = () => {
    if (!deleteTargetId) return;
    try {
      deleteClient(deleteTargetId);
      addToast("Client deleted permanently", "success");
    } catch {
      addToast("Could not delete this client", "error");
    } finally {
      setDeleteTargetId(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-end mb-4">
        <Button
          onClick={() => {
            setEditingClientId(null);
            setShowAddForm(!showAddForm);
            setNewName("");
            setNewColor("#10b981");
            setNewWeeklyTargetDays(0);
          }}
          variant={showAddForm ? "default" : "primary"}
        >
          {showAddForm ? "Cancel" : "Add Client"}
        </Button>
      </div>

      {/* Add/Edit Form */}
      {(showAddForm || editingClientId) && (
        <Card sub className="mb-6 p-4">
          <h3 className="text-lg font-black mb-3 dark:text-cream">
            {editingClientId ? "Edit Client" : "Add New Client"}
          </h3>
          <div className="space-y-3">
            <FormField label="Name" htmlFor="client-name-input">
              <Input
                id="client-name-input"
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Client name"
              />
            </FormField>

            <div>
              <label className="block text-sm font-bold text-ink dark:text-cream mb-1">
                Color
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={newColor}
                  onChange={(e) => setNewColor(e.target.value)}
                  className="h-10 w-12 border-2 border-ink dark:border-cream rounded cursor-pointer"
                />
                <Input
                  type="text"
                  value={newColor}
                  onChange={(e) => setNewColor(e.target.value)}
                  className="flex-1"
                  placeholder="#RRGGBB"
                />
              </div>
            </div>

            <FormField
              label="Weekly Target Days"
              htmlFor="client-weekly-target-input"
            >
              <Input
                id="client-weekly-target-input"
                type="number"
                min="0"
                step="0.5"
                value={newWeeklyTargetDays}
                onChange={(e) =>
                  setNewWeeklyTargetDays(
                    e.target.value === "" ? "" : Number(e.target.value),
                  )
                }
                placeholder="0"
              />
            </FormField>

            <div className="flex justify-end pt-2">
              <Button
                onClick={
                  editingClientId ? handleUpdateClient : handleAddClient
                }
                variant="primary"
              >
                {editingClientId ? "Update" : "Add"}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Active clients */}
      {activeClients.length === 0 ? (
        <p className="text-ink-soft dark:text-cream/70">
          No active clients. Add one to start tracking time.
        </p>
      ) : (
        <>
          {activeClients.length > 1 && (
            <p className="text-xs text-ink-soft dark:text-cream/60 mb-2">
              Drag the grip to set the order clients appear in when you stop a
              timer.
            </p>
          )}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={activeClients.map((c) => c.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {activeClients.map((client) => (
                  <SortableClientRow
                    key={client.id}
                    client={client}
                    onEdit={startEdit}
                    onArchive={handleArchive}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </>
      )}

      {/* Archived clients */}
      {archivedClients.length > 0 && (
        <div className="mt-6">
          <button
            onClick={() => setShowArchived(!showArchived)}
            className="flex items-center gap-2 text-sm font-black text-ink dark:text-cream uppercase tracking-wider hover:underline underline-offset-4"
            aria-expanded={showArchived}
          >
            <svg
              className={`w-3 h-3 transition-transform ${showArchived ? "rotate-90" : ""}`}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path
                d="M9 5l7 7-7 7"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Archived ({archivedClients.length})
          </button>

          {showArchived && (
            <div className="space-y-2 mt-3">
              {archivedClients.map((client) => (
                <Card
                  sub
                  key={client.id}
                  className="flex items-center justify-between p-3 opacity-60"
                >
                  <div className="flex items-center">
                    <div
                      className="w-5 h-5 rounded-full mr-3 border-2 border-ink dark:border-cream"
                      style={{ backgroundColor: client.color }}
                    />
                    <div className="flex flex-col">
                      <span className="font-bold text-ink dark:text-cream line-through">
                        {client.name}
                      </span>
                      <span className="text-xs text-ink-soft dark:text-cream/60">
                        Archived{" "}
                        {client.archivedAt
                          ? new Date(client.archivedAt).toLocaleDateString()
                          : ""}
                      </span>
                    </div>
                  </div>

                  <div className="flex gap-1.5">
                    <Button
                      onClick={() => handleRestore(client.id)}
                      icon
                      title="Restore"
                      aria-label="Restore client"
                    >
                      {RESTORE_ICON}
                    </Button>
                    <Button
                      onClick={() => setDeleteTargetId(client.id)}
                      icon
                      variant="danger"
                      title="Delete permanently"
                      aria-label="Delete client permanently"
                    >
                      {TRASH_ICON}
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Client preview */}
      {(showAddForm || editingClientId) && (
        <div className="mt-4 pt-4 border-t-2 border-ink/30 dark:border-cream/30">
          <h3 className="text-sm font-bold text-ink dark:text-cream mb-2">
            Preview
          </h3>
          <div className="flex items-center">
            <Badge
              className="mr-2"
              style={{
                backgroundColor: newColor,
                color: "var(--color-ink)",
              }}
            >
              {newName || "Client name"}
            </Badge>
            <span className="text-sm text-ink-soft dark:text-cream/60">
              How it will appear in your time entries
            </span>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={!!deleteTargetId}
        title="Delete client permanently"
        message={
          deleteTarget
            ? deleteTargetEntryCount > 0
              ? `This will permanently delete "${deleteTarget.name}" and ${deleteTargetEntryCount} time ${deleteTargetEntryCount === 1 ? "entry" : "entries"} associated with it. This cannot be undone.`
              : `This will permanently delete "${deleteTarget.name}". This cannot be undone.`
            : ""
        }
        confirmLabel="Delete permanently"
        variant="danger"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTargetId(null)}
      />
    </div>
  );
};

export default ClientManager;
